/**
 * Which organization the dashboard is looking at, and what the person may do
 * in it.
 *
 * Deliberately free of React and of `window`. Everything here is a pure
 * function over data the API returned plus a `Storage` handed in, which is what
 * lets the role ladder and the switcher's ordering be tested without a browser.
 * The screens do the rendering; this file does the deciding.
 *
 * **The role ladder here is a mirror, not a source.** The platform enforces
 * every one of these rules server-side (`packages/domain/src/membership.ts` is
 * the original), and a member who edits their own JavaScript gains nothing but
 * a 403. What it buys is a screen that does not offer somebody a button that
 * will refuse them, which is a different and much smaller claim than security.
 * When the two disagree the server wins, and the fix belongs here.
 */

/** The four roles, lowest first. Matches `org_members.role`'s CHECK. */
export type OrgRole = "billing" | "member" | "admin" | "owner";

/** Not exported: nothing outside this file names a kind, it only reads `Org`. */
type OrgKind = "personal" | "team";

export interface Org {
  readonly id: string;
  readonly name: string;
  readonly kind: OrgKind;
  readonly plan: string;
  readonly role: OrgRole;
  readonly seats: number;
  readonly suspended: boolean;
  readonly createdAt: string;
}

/** billing < member < admin < owner, as `ORG_ROLE_RANK` orders them server-side. */
const ROLE_RANK: Readonly<Record<OrgRole, number>> = {
  billing: 0,
  member: 1,
  admin: 2,
  owner: 3,
};

/** How each role is described to the person holding it. */
export const ROLE_LABELS: Readonly<Record<OrgRole, string>> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  billing: "Billing",
};

/** What each role is for, one line, shown beside the picker on the invite form. */
export const ROLE_DESCRIPTIONS: Readonly<Record<OrgRole, string>> = {
  owner: "Everything, including billing, roles and deleting the organisation.",
  admin: "Manage sites, people and settings. Not billing.",
  member: "Create and manage sites. Cannot invite or remove people.",
  billing: "Invoices and the subscription only. Sees no sites.",
};

/**
 * A role name from a `<select>`, or nothing.
 *
 * A lookup rather than a cast. `event.target.value` is a `string` as far as the
 * DOM is concerned, and asserting it into `OrgRole` would keep compiling on the
 * day somebody adds an `<option>` with a typo in it; this returns `undefined`
 * instead, and the caller does nothing rather than sending the platform a role
 * it will reject.
 */
const ROLE_BY_NAME: Readonly<Record<string, OrgRole | undefined>> = {
  owner: "owner",
  admin: "admin",
  member: "member",
  billing: "billing",
};

export function parseOrgRole(value: string): OrgRole | undefined {
  return ROLE_BY_NAME[value];
}

/** Does `role` clear the bar `minRole` sets. */
export function roleAtLeast(role: OrgRole, minRole: OrgRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minRole];
}

/**
 * May this role reach sites at all.
 *
 * The `billing` seat is the one that matters: a finance contact exists so an
 * invoice reaches somebody who is not the person publishing board papers, and
 * giving them a seat must not give them the papers. The server refuses them
 * `GET /api/sites` with a 404; this is what stops the screen asking.
 */
export function canSeeSites(role: OrgRole): boolean {
  return roleAtLeast(role, "member");
}

/** May this role invite, change roles and remove people. */
export function canManageMembers(role: OrgRole): boolean {
  return roleAtLeast(role, "admin");
}

/**
 * May this role create or remove an owner.
 *
 * Separate from `canManageMembers` because the server's rule is: an admin may
 * move anybody between the three lower roles, but only an owner may make or
 * unmake an owner. An admin offered an "Owner" option would be offered a
 * refusal.
 */
export function canManageOwners(role: OrgRole): boolean {
  return roleAtLeast(role, "owner");
}

/** The roles this person may move somebody else to. */
export function assignableRoles(actorRole: OrgRole): OrgRole[] {
  const roles: OrgRole[] = ["admin", "member", "billing"];
  return canManageOwners(actorRole) ? ["owner", ...roles] : roles;
}

/**
 * The switcher's order: the personal org first, then teams by name.
 *
 * Personal first because it is the one that always exists and the one somebody
 * lands in with no memory of a choice; teams alphabetically because that is the
 * order somebody scanning a list expects, and creation order is meaningless to
 * everyone but the person who did the creating. `localeCompare` rather than
 * `<`, so "Ålesund" sorts where a reader expects rather than after "Zurich".
 */
export function sortOrgs(orgs: readonly Org[]): Org[] {
  return orgs.toSorted((a, b) => {
    if (a.kind !== b.kind) return a.kind === "personal" ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/** Where the remembered org id lives. Namespaced: the origin is shared with nothing. */
export const ACTIVE_ORG_KEY = "lagoon.activeOrgId";

/**
 * A `localStorage` that cannot throw.
 *
 * Storage access throws outright in a few real configurations (Safari's private
 * mode historically, and any browser set to block site data), and the dashboard
 * failing to render because it could not remember a dropdown would be a poor
 * trade. Every read falls back to "nothing remembered", which is a state the
 * caller already handles.
 */
export interface OrgMemory {
  read(): string | null;
  write(orgId: string): void;
}

export function storageMemory(storage: Storage | undefined): OrgMemory {
  return {
    read() {
      try {
        return storage?.getItem(ACTIVE_ORG_KEY) ?? null;
      } catch {
        return null;
      }
    },
    write(orgId) {
      try {
        storage?.setItem(ACTIVE_ORG_KEY, orgId);
      } catch {
        // Remembering is a convenience; failing to is not worth a broken screen.
      }
    },
  };
}

/** The browser's `localStorage`, or a memory that forgets, outside one. */
export function browserMemory(): OrgMemory {
  return storageMemory(typeof localStorage === "undefined" ? undefined : localStorage);
}

/**
 * Which org to show, given what the API returned and what was remembered.
 *
 * The remembered id is a hint and is re-checked against the list every time:
 * somebody removed from an org must not keep looking at it because their
 * browser remembers it, and the list is the server's answer to "which orgs are
 * yours". An id that is no longer there falls back to the first in switcher
 * order, which is the personal org.
 */
export function pickActiveOrg(orgs: readonly Org[], remembered: string | null): Org | undefined {
  const ordered = sortOrgs(orgs);
  const found = remembered === null ? undefined : ordered.find((org) => org.id === remembered);
  return found ?? ordered[0];
}
